import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { TalentPremiumBenefits, TalentPremiumHero } from '@components/templates/talent/premium-page';
import Faqs from '@components/templates/talent/Faqs/Faqs';
import { ApiUserSubscriptionList, ApiUserSubscriptionPaymentDetails } from '@type/v1-api-types/user-subscription';
import { TalentPremiumSubscriptionInfo, TalentPremiumInternalApplicationSummary } from '@components/templates/talent/premium-page/has-subscription';
import { useSwrPaymentDetails, useSwrUserSubscriptionList } from '@hooks/useSwrApi/endpoints/user-subscription';
import { gsspFetchData, gsspCheckCookieAuth, gsspGetLocale } from '@utils/gssp';

// Interface
// ************************************************************************************
interface PremiumTalentProps {
  isAuth: boolean,
  locale: Locale,
  serverSubscriptionList?: ApiUserSubscriptionList,
  serverPaymentDetails?: ApiUserSubscriptionPaymentDetails,
}


// Page component
// ************************************************************************************
const PremiumTalent = (props: PremiumTalentProps) => {
  // Destructure props
  const {
    isAuth,
    locale,
    serverSubscriptionList,
    serverPaymentDetails,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // SWR data
  // *************************************
  const { data: subscriptionList } = useSwrUserSubscriptionList({ locale, serverSideData: serverSubscriptionList, shouldFetch: isAuth });
  const { data: paymentDetails } = useSwrPaymentDetails({ locale, serverSideData: serverPaymentDetails, shouldFetch: isAuth });


  // Helper variables
  // *************************************
  const hasPremium = subscriptionList && subscriptionList.length > 0;
  const currentSubscription = hasPremium ? subscriptionList?.[0] : undefined;


  // Render page component
  // *************************************
  return (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>
          {t('premium.talent.meta.title')}
        </title>
        <meta name="description" content={t('premium.talent.meta.description')} />
      </Head>


      {/* PAGE CONTENT */}
      <div className={`${hasPremium ? 'bg-surface-100' : 'bg-gradient-to-b from-green-50 via-white to-green-50'} grow transition-all duration-200`}>
        {/* Current subscription info */
          hasPremium && (
            <TalentPremiumSubscriptionInfo
              locale={locale}
              currentSubscription={currentSubscription}
              paymentDetails={paymentDetails}
            />
          )
        }

        {/* Current subscription stats */
          hasPremium && (
            <TalentPremiumInternalApplicationSummary
              locale={locale}
              userSubscriptionList={subscriptionList}
            />
          )
        }

        {/* Hero - no subscription */
          !hasPremium && (
            <TalentPremiumHero
              locale={locale}
              paymentDetails={paymentDetails}
              isAuth={isAuth}
            />
          )
        }

        {/* Benefits list */}
        <div className="container mb-16 mt-10 md:my-16">
          <TalentPremiumBenefits className="text-center" />
        </div>

        {/* FAQ's */
          !hasPremium && (
            <div className="container mb-5 lg:mb-10">
              <Faqs />
            </div>
          )
        }
      </div>
    </>
  );
};


/**
 * @description Get the server-side props
 * @param context - The context object
 */
export const getServerSideProps: GetServerSideProps = async (context) => {
  // Props destructuring
  const {
    params,
    req,
  } = context;

  const locale = gsspGetLocale(params);

  // Check if the user is authenticated
  // *********************************
  const isAuth = gsspCheckCookieAuth({ req });

  // Fetch data conditionally
  // *********************************
  const endpoints = [isAuth ? '/v1/user-subscription/list' : '', isAuth ? '/v1/user-subscription/payment-details' : ''];
  const [serverSubscriptionList, serverPaymentDetails] = await gsspFetchData({ endpoints, req, locale });

  // Return props
  // *********************************
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      isAuth,
      locale,
      serverSubscriptionList,
      serverPaymentDetails,
    },
  };
};


/**
 * @description Export the page component.
 */
export default PremiumTalent;
